/* eslint-disable @next/next/no-img-element */

/* eslint-disable react/no-unescaped-entities */
import Head from 'next/head.js';
import Image from 'next/image';

import { AppFooter } from '@/components/AppFooter.js';
import { TemplateLanding } from '@/components/TemplateLanding.js';
import {
  ArrowPathIcon,
  BeakerIcon,
  BoltIcon,
  CheckCircleIcon,
  CloudIcon,
  LightBulbIcon,
  PlayIcon,
  PuzzlePieceIcon,
  SparklesIcon,
  TagIcon,
} from '@heroicons/react/20/solid';
import { Square3Stack3DIcon } from '@heroicons/react/24/solid';

export default function Roadmap() {
  return (
    <TemplateLanding pageName="Roadmap">
      <Head>
        <meta property="og:image" content="/assets/graphics/roadmap-here.png" />
      </Head>
      <div className="relative isolate overflow-hidden bg-gray-900 pt-8 sm:pt-12 pb-8 sm:pb-12">
        <Image
          alt=""
          width="1900"
          height="1080"
          src="https://images.unsplash.com/photo-1635377090186-036bca445c6b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA&auto=format&fit=crop&crop=focalpoint&fp-y=.8&w=1900&h=1080&q=80"
          className="absolute top-0 bottom-0 left-0 right-0 inset-0 -z-10 h-full w-full object-cover opacity-10"
        />
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-0 max-w-2xl">
            <p className="text-base font-semibold leading-7 text-brand-dark-pink">Roadmap</p>
            <h1 className="text-4xl font-bold tracking-tight text-white sm:text-6xl">
              From here to there
            </h1>
            <p className="mt-6 text-xl leading-8 text-white">
              [3d]pages has been a labor of love. We&apos;ve been on a mission to make the most
              simple &amp; portable 3d creation tool ever. We&apos;re the 3d sketchpad of the
              future, and we&apos;re just getting started.
            </p>
          </div>
        </div>
      </div>
      <div className="bg-white pt-2 pb-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 text-base leading-7 text-gray-700">
          <div className="mt-10">
            <div className="flex flex-col lg:flex-row">
              <div className="flex-1">
                <p className="font-bold">
                  Here are some of the features we&apos;ve built thus far:
                </p>
                <ul role="list" className="mt-8 max-w-xl space-y-8 text-gray-600">
                  <li className="flex gap-x-3">
                    <CheckCircleIcon
                      aria-hidden="true"
                      className="mt-1 h-5 w-5 flex-none text-brand-dark-pink"
                    />
                    <span>
                      <strong className="font-semibold text-gray-900">
                        Shapes, Models, and Configurable Lighting
                      </strong>{' '}
                      – All the basic objects you need need to create a scene.
                    </span>
                  </li>
                  <li className="flex gap-x-3">
                    <CheckCircleIcon
                      aria-hidden="true"
                      className="mt-1 h-5 w-5 flex-none text-brand-dark-pink"
                    />
                    <span>
                      <strong className="font-semibold text-gray-900">Simplified Editing</strong> –
                      Rather than look to 3d modeling software, we turned to games. Simplicity, for
                      us, looks like Zelda or Minecraft as opposed to 3DS, Blender, or Unity/Unreal.
                    </span>
                  </li>
                  <li className="flex gap-x-3">
                    <CheckCircleIcon
                      aria-hidden="true"
                      className="mt-1 h-5 w-5 flex-none text-brand-dark-pink"
                    />
                    <span>
                      <strong className="font-semibold text-gray-900">Collaborate</strong> – Work
                      together with friends or colleagues to create something amazing. As you make
                      edits others can see your scene change in real time!
                    </span>
                  </li>
                  <li className="flex gap-x-3">
                    <CheckCircleIcon
                      aria-hidden="true"
                      className="mt-1 h-5 w-5 flex-none text-brand-dark-pink"
                    />
                    <span>
                      <strong className="font-semibold text-gray-900">Sharable</strong> – Want to
                      share your scene with the world? Just send them a link.
                    </span>
                  </li>
                  <li className="flex gap-x-3">
                    <CheckCircleIcon
                      aria-hidden="true"
                      className="mt-1 h-5 w-5 flex-none text-brand-dark-pink"
                    />
                    <span>
                      <strong className="font-semibold text-gray-900">Exportable</strong> – Download
                      to JSON (to use in a game engine) or straight to HTML/ThreeJS to publish on
                      the web.
                    </span>
                  </li>
                  <li className="flex gap-x-3">
                    <CheckCircleIcon
                      aria-hidden="true"
                      className="mt-1 h-5 w-5 flex-none text-brand-dark-pink"
                    />
                    <span>
                      <strong className="font-semibold text-gray-900">Collision Detection</strong> –
                      In play mode, we use BVH (bounding volume hierarchy) to make sure users can't
                      walk right through objects and fall through the ground. This scalable approach
                      means scenes load super quick too!
                    </span>
                  </li>
                </ul>
              </div>
              <div className="lg:flex-1 lg:p-12 pt-8 lg:pt-12 flex items-center justify-center">
                <Image
                  alt=""
                  width="600"
                  height="300"
                  src="/assets/graphics/roadmap-here.png"
                  className="object-cover rounded-xl"
                />
              </div>
            </div>
            <h2 className="mt-16 text-2xl font-bold tracking-tight text-gray-900">
              What&apos;s next?
            </h2>
            <p className="mt-6">
              Oh so much! We&apos;re going to do everything from allow creators to add interactivity
              to their scenes to integrating Gen AI.
            </p>
            <div className="mt-10 max-w-2xl">
              <ul role="list" className="mt-8 max-w-xl space-y-8 text-gray-600">
                <li className="flex gap-x-3">
                  <ArrowPathIcon
                    aria-hidden="true"
                    className="mt-1 h-5 w-5 flex-none text-brand-dark-pink"
                  />
                  <span>
                    <strong className="font-semibold text-gray-900">
                      Undo/Redo, Copy/Paste, Favorites, and Asset Management
                    </strong>{' '}
                    – With this first launch we skipped over some of the basics. We're going to add
                    in more tools to make it quick to manage your scenes.
                  </span>
                </li>
                <li className="flex gap-x-3">
                  <TagIcon
                    aria-hidden="true"
                    className="mt-1 h-5 w-5 flex-none text-brand-dark-pink"
                  />
                  <span>
                    <strong className="font-semibold text-gray-900">Object Tagging</strong> – Tags
                    will give other game engines about how objects should work.
                  </span>
                </li>
                <li className="flex gap-x-3">
                  <PlayIcon
                    aria-hidden="true"
                    className="mt-1 h-5 w-5 flex-none text-brand-dark-pink"
                  />
                  <span>
                    <strong className="font-semibold text-gray-900">
                      Interactivity and Animations
                    </strong>{' '}
                    – GLTF/GLB have animations included right in the model. With a few hooks our
                    editor can make scenes come alive.
                  </span>
                </li>
                <li className="flex gap-x-3">
                  <BeakerIcon
                    aria-hidden="true"
                    className="mt-1 h-5 w-5 flex-none text-brand-dark-pink"
                  />
                  <span>
                    <strong className="font-semibold text-gray-900">
                      Water, Fog, Effect Emitters, and More
                    </strong>{' '}
                    – What's interactivity without special effects - fire, rain, snow, and more!
                  </span>
                </li>
                <li className="flex gap-x-3">
                  <PuzzlePieceIcon
                    aria-hidden="true"
                    className="mt-1 h-5 w-5 flex-none text-brand-dark-pink"
                  />
                  <span>
                    <strong className="font-semibold text-gray-900">Scripting</strong> – A little
                    bit of code can go a long way.
                  </span>
                </li>
                <li className="flex gap-x-3">
                  <LightBulbIcon
                    aria-hidden="true"
                    className="mt-1 h-5 w-5 flex-none text-brand-dark-pink"
                  />
                  <span>
                    <strong className="font-semibold text-gray-900">
                      Spotlights, Box Area Lights, Mirrors, and Cascading Shadow Maps
                    </strong>{' '}
                    – Better techniques to make lights and shadows look more realistic.
                  </span>
                </li>
                <li className="flex gap-x-3">
                  <CloudIcon
                    aria-hidden="true"
                    className="mt-1 h-5 w-5 flex-none text-brand-dark-pink"
                  />
                  <span>
                    <strong className="font-semibold text-gray-900">Point Clouds</strong> – Point
                    clouds are a crucial part of NERF and scene reconstruction.
                  </span>
                </li>
                <li className="flex gap-x-3">
                  <Square3Stack3DIcon
                    aria-hidden="true"
                    className="mt-1 h-5 w-5 flex-none text-brand-dark-pink"
                  />
                  <span>
                    <strong className="font-semibold text-gray-900">Templates</strong> – Grassy
                    fields, islands, deserts, cities and outer space. We&apos;re going to create the
                    best launching point for your scene. Of course, you can always risk it all and
                    start from scratch.
                  </span>
                </li>
                <li className="flex gap-x-3">
                  <SparklesIcon
                    aria-hidden="true"
                    className="mt-1 h-5 w-5 flex-none text-brand-dark-pink"
                  />
                  <span>
                    <strong className="font-semibold text-gray-900">Generative AI Models</strong> –
                    We&apos;re big fans of Luma and have been following Meta&apos;s latest
                    advancements in 3D Generative AI. We&apos;re excited to integrate these features
                    right into the platform.
                  </span>
                </li>
                <li className="flex gap-x-3">
                  <BoltIcon
                    aria-hidden="true"
                    className="mt-1 h-5 w-5 flex-none text-brand-dark-pink"
                  />
                  <span>
                    <strong className="font-semibold text-gray-900">
                      Model Simplification and Performance Tuning
                    </strong>{' '}
                    – Since we're on the web we need to make things fast. We'll be optimizing models
                    to make scenes load quickly with the most minimal amount of data transfer
                    possible. Today we rely on some sensible limits, but we'll be adding more tools
                    that automatically optimize scenes.
                  </span>
                </li>
              </ul>
            </div>
            <p className="mt-6">
              And more - we'll be continuing to tune our UX and make what we have more robust in all
              the <i>right</i> ways.{' '}
              <a className="underline" href="https://forms.gle/qX9XU3aHNTZeyFC37" target="_blank">
                Got ideas? Shoot them our way!
              </a>
            </p>
            <h2 className="mt-6">We're so excited to see what you create! - The [3d]pages team</h2>
          </div>
        </div>
      </div>
      <AppFooter />
    </TemplateLanding>
  );
}
